/**
 * useCmsOverrides — slot overrides for the active locale, fresher than the bundle.
 *
 * Snapshot seeds state; `/api/cms/public/overrides?locale=` is MERGED into it
 * on mount so an operator's slot-text edit shows without a rebuild.
 *
 * The public API returns a FLAT map (`{ "home::x": "A" }`, one locale only)
 * while the snapshot is NESTED per locale
 * (`{ "home::x": { "zh-Hant": …, "zh-Hans": …, en: … } }`).
 * Replacing the snapshot with the flat payload therefore made every lookup
 * miss (see `src/cms/overrideMap.ts`).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsOverrides, type CmsOverride } from '@/content/cms/overrides.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'
import { normalizeOverrideMap, type OverrideMap } from '@/cms/overrideMap'

interface OverridesResp {
  overrides?: Record<string, unknown>
}

export function useCmsOverrides(): Record<string, CmsOverride> {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = (cmsData?.overrides ?? cmsOverrides) as OverrideMap
  const [overrides, setOverrides] = useState<OverrideMap>(initial)

  useEffect(() => {
    if (cmsData?.overrides) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/overrides?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<OverridesResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.overrides || Object.keys(j.overrides).length === 0) return
        // MERGE, never replace — the other locales keep their snapshot values.
        setOverrides((prev) => normalizeOverrideMap(j.overrides, locale, prev))
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.overrides])

  return overrides as Record<string, CmsOverride>
}
